import type { ObjectiveConfig } from '@/types/api/objective-params'

/**
 * Structural shape of anything that carries objective params: a model's
 * FixedIncomeStrategy or a proposal-level Model.
 */
export interface ObjectiveParamsSource {
  objective_strategy?: ObjectiveConfig['objective_strategy']
  buy_objective_terms?: unknown[]
  sell_objective_terms?: unknown[]
}

/**
 * Extracts objective config fields from a strategy or model.
 * Returns undefined if the source has no objective terms.
 */
export function extractObjectiveConfig(
  strategy: ObjectiveParamsSource | undefined,
): ObjectiveConfig | undefined {
  if (!strategy?.buy_objective_terms?.length) return undefined
  const buyTerms =
    strategy.buy_objective_terms as unknown as ObjectiveConfig['buy_objective_terms']
  const sellTerms = strategy.sell_objective_terms?.length
    ? {
        sell_objective_terms:
          strategy.sell_objective_terms as unknown as ObjectiveConfig['sell_objective_terms'],
      }
    : {}
  return {
    objective_strategy: strategy.objective_strategy,
    buy_objective_terms: buyTerms,
    ...sellTerms,
  }
}

/**
 * Returns the model-persisted objective config if it has terms,
 * otherwise falls back to the default (Statsig) config.
 */
export function resolveObjectiveConfig(
  modelConfig: ObjectiveConfig | undefined,
  defaultConfig: ObjectiveConfig,
): ObjectiveConfig {
  return modelConfig ?? defaultConfig
}
